import { useState, useEffect } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Product, ProductOverview, ProductType } from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { getAllProducts } from "../../services/ProductService";
import { getNaturalStockReport } from "../../services/FinishingCenterService";
import { useApplicationContext } from "../../application/ApplicationContext";
import "./NaturalProductsAmountReportPage.scss";

export const NaturalProductsAmountReportPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [mercantileNaturalOverview, setMercantileNaturalOverview] = useState<
    ProductOverview[]
  >([]);

  const [processing, setProcessing] = useState(false);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
        const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);

        const pResp: AxiosResponse = await getAllProducts();
        ensureSuccess(pResp);
        const pr: Product[] = pResp?.data?.result ?? [];
        setProductsApi(pr);

        const resp: AxiosResponse = await getNaturalStockReport(
          yearStart,
          yearEnd,
        );
        const docs = resp.data?.result ?? [];
        formatMercantileOverviewData(docs, pr);
      } catch (error) {
        console.log(error as AxiosError);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [businessYear]);

  const formatMercantileOverviewData = (docs: any[], pr: Product[]) => {
    const overviewMap = new Map<string, ProductOverview>();

    for (const doc of docs) {
      if (!doc.product) continue;
      if (doc.finishedDocument !== true) continue;
      if (doc.isNatural === false) continue;

      const key = doc.product.id;
      const existing = overviewMap.get(key);

      const netto = Number(doc.netto ?? 0);
      const sign = doc.input ? 1 : -1; // ulaz +, izlaz -

      const productMeta = pr.find(
        (p) => String(p.id) === String(doc.product.id),
      );

      if (existing) {
        existing.amount = (existing.amount ?? 0) + sign * netto;
      } else {
        overviewMap.set(key, {
          id: doc.id,
          code: doc.product.code ?? productMeta?.code ?? "",
          name: (doc.product.name ?? productMeta?.name ?? "") as string,
          year: undefined,
          type: ProductType.MercantileGoods,
          product: doc.product as Product,
          mark: doc.product.measuringUnit ?? productMeta?.measuringUnit ?? "",
          amount: sign * netto,
        });
      }
    }

    const allData = Array.from(overviewMap.values());
    const filteredData = allData.filter((item) => {
      const amountValue = item.amount ?? 0;
      return Math.abs(amountValue) > 0.0001;
    });

    setMercantileNaturalOverview(filteredData);
  };

  const amount = (rowData: ProductOverview) => {
    return Utility.addThousandSeparator(rowData?.amount as number);
  };

  const exportToExcel = () => {
    const excelMercantileObject = mercantileNaturalOverview.map(
      (item: ProductOverview) => {
        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          MJ:
            productsApi.find((p) => p.id === item?.product?.id)
              ?.measuringUnit ??
            item?.mark ??
            "",
          Stanje: item.amount ?? "",
        };
      },
    );
    let excelObject: any[] = [];
    const emptyRow = {
      SifraRobe: "",
      Naziv: "",
      MJ: "",
      Stanje: "",
    };
    excelObject = [emptyRow, ...excelMercantileObject];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 3 }); // Kolona D (Stanje)
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Numerički tip
            // Dinamički format u zavisnosti od toga da li je ceo broj
            if (Number.isInteger(worksheet[cellAddress].v)) {
              worksheet[cellAddress].z = "0"; // Format za cele brojeve
            } else {
              worksheet[cellAddress].z = "0.00"; // Format za decimale
            }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Stanje naturalne robe na dan ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-ic"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                marginTop: "0.5rem",
              }}
            ></i>
            <span className="header-title">{t("Stanje naturalne robe")}</span>
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
            />
          </div>
        </div>
        <div className="table-body">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              value={mercantileNaturalOverview}
              showGridlines
              stripedRows
            >
              <Column field="code" header={t("Šifra robe")} />
              <Column field="name" header={t("Naziv")} />
              <Column body={amount} header={t("Stanje")} />
              <Column field="mark" header={t("M.J.")} />
            </DataTable>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
